import { ChangeDetectionStrategy, Component, DoCheck, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { FormField } from '../../../interfaces/form.interface';
import { DropdownData, FilteredValue, InputType, type ReactiveLazyDropdownFilterRequest } from '../../../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../../../model/reactive-form-value.utils';

const DEFAULT_SELECT_PLACEHOLDER = 'Seleccionar';
const PANEL_RESTORE_DELAY_MS = 0;
const DEFAULT_FILTER_MATCH_LIMIT = 50;
const DEFAULT_FILTER_MIN_LENGTH = 0;
const DEFAULT_REMOTE_FILTER_MIN_LENGTH = 2;
const DEFAULT_REMOTE_FILTER_DEBOUNCE_MS = 250;

type ReactivePrimeDropdownPanel = {
    show?: () => void;
    filterValue?: string | null;
};

@Component({
    selector: 'app-reactive-select-field',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './select-field.component.html',
    styleUrls: ['./select-field.component.scss'],
})
export class ReactiveSelectFieldComponent<T> implements DoCheck {
    @Input() field!: FormField<T>;

    @Input() model!: FormGroup;

    @Input() isEditable = false;

    @Input() describedById = '';

    @Input() isInvalid = false;

    @Output() public lazyDropdownRequested = new EventEmitter<string>();

    @Output() public lazyDropdownFilterRequested = new EventEmitter<ReactiveLazyDropdownFilterRequest>();

    @Input() filterInputParcelas = false;

    @ViewChild('reactiveSelect') private reactiveSelect: ReactivePrimeDropdownPanel | null = null;

    @ViewChild('reactiveMultiselect') private reactiveMultiselect: ReactivePrimeDropdownPanel | null = null;

    public readonly inputType = InputType;

    public isPanelOpen = false;

    public lastFilterValue = '';

    private lastOptionsSignature = '';

    private lastSelectedOptionsSignature = '';

    private lastSelectedOptions: FilteredValue<T>[] = [];

    private isRestoreScheduled = false;

    private shouldRestoreAfterReload = false;

    private remoteFilterTimer: ReturnType<typeof setTimeout> | null = null;

    public get filteredOptions(): FilteredValue<T>[] {
        return (this.field?.input?.data as DropdownData<T>).filtered ?? [];
    }

    public get renderedOptions(): FilteredValue<T>[] {
        const options = this.filteredOptions;
        const dropdownConfiguration = this.field.input?.dropdownConfiguration;
        const minFilterLength = dropdownConfiguration?.minFilterLengthBeforeFullRender ?? DEFAULT_FILTER_MIN_LENGTH;
        const maxRenderedOptions = dropdownConfiguration?.maxRenderedOptionsBeforeFilter ?? options.length;

        if (options.length <= maxRenderedOptions) {
            return options;
        }

        const normalizedFilter = this.normalizeFilterValue(this.lastFilterValue);

        if (normalizedFilter.length >= minFilterLength && normalizedFilter.length > DEFAULT_FILTER_MIN_LENGTH) {
            return this.findRenderedOptionsByFilter(options, normalizedFilter, maxRenderedOptions);
        }

        return this.buildLimitedRenderedOptions(options, maxRenderedOptions);
    }

    public get virtualScrollEnabled(): boolean {
        return this.field.input?.dropdownConfiguration?.virtualScroll ?? this.renderedOptions.length > DEFAULT_FILTER_MATCH_LIMIT;
    }

    public get virtualScrollItemSize(): number {
        return this.field.input?.dropdownConfiguration?.virtualScrollItemSize ?? 40;
    }

    public get dataKey(): string | null {
        const firstRealOption = this.filteredOptions.find((option) => option.value !== null && option.value !== undefined);

        if (!firstRealOption) {
            return ReactiveFormValueUtils.getFieldControlValueKey(this.field);
        }

        return typeof firstRealOption.value === 'object'
            ? ReactiveFormValueUtils.getFieldControlValueKey(this.field)
            : null;
    }

    public ngDoCheck(): void {
        const signature = this.createOptionsSignature();

        if (signature === this.lastOptionsSignature) {
            return;
        }

        const shouldRestorePanel = this.shouldRestorePanelAfterOptionsChange();
        this.lastOptionsSignature = signature;

        if (shouldRestorePanel) {
            this.shouldRestoreAfterReload = true;
            this.schedulePanelRestore();
        }
    }

    public getSelectPlaceholder(): string {
        const selectedLabel = this.resolveSelectedControlLabel();

        return selectedLabel !== '' ? selectedLabel : DEFAULT_SELECT_PLACEHOLDER;
    }

    public onPanelShow(): void {
        this.isPanelOpen = true;
        this.requestLazyDropdownLoad();
    }

    private requestLazyDropdownLoad(): void {
        const fieldName = String(this.field?.name ?? '').trim();

        if (fieldName === '') {
            return;
        }

        this.lazyDropdownRequested.emit(fieldName);
    }

    public onPanelHide(): void {
        if (this.shouldRestoreAfterReload) {
            return;
        }

        this.isPanelOpen = false;
    }

    public onFilter(event: unknown): void {
        const filterValue = (event as { readonly filter?: unknown })?.filter;

        this.lastFilterValue = String(filterValue ?? '');
        this.scheduleRemoteFilterRequest(this.lastFilterValue);
    }


    private scheduleRemoteFilterRequest(filterValue: string): void {
        const remoteFilter = this.field.input?.dropdownConfiguration?.remoteFilter;

        if (remoteFilter?.enabled !== true) {
            return;
        }

        const fieldName = String(this.field?.name ?? '').trim();
        const normalizedFilter = filterValue.trim();
        const minLength = remoteFilter.minLength ?? DEFAULT_REMOTE_FILTER_MIN_LENGTH;

        if (fieldName === '' || normalizedFilter.length < minLength) {
            return;
        }

        if (this.remoteFilterTimer !== null) {
            clearTimeout(this.remoteFilterTimer);
        }

        this.remoteFilterTimer = setTimeout((): void => {
            this.remoteFilterTimer = null;
            this.lazyDropdownFilterRequested.emit({ fieldName, filter: normalizedFilter });
        }, remoteFilter.debounceMs ?? DEFAULT_REMOTE_FILTER_DEBOUNCE_MS);
    }

    public shouldUseExternalFilter(): boolean {
        return this.field.input?.dropdownConfiguration?.minFilterLengthBeforeFullRender !== undefined;
    }

    public updateModel(): void {
        if (!this.field.input?.selectButtonConfiguration?.onClick) {
            return;
        }

        const onClick = this.field.input.selectButtonConfiguration.onClick(this.field as T);

        onClick.then((data) => {
            Object.entries(data).forEach(([key, value]) => {
                this.model.patchValue({ [key]: value });
            });

            return;
        }).catch((error: string) => {
            console.log(error);
        });
    }

    private resolveSelectedControlLabel(): string {
        const fieldName = this.field?.name ?? '';
        const controlValue = fieldName !== '' ? this.model?.get(fieldName)?.value : null;

        if (!ReactiveFormValueUtils.hasNonEmptyValue(controlValue)) {
            return '';
        }

        const matchedOptionLabel = this.resolveMatchedOptionLabel(controlValue);

        if (matchedOptionLabel !== '') {
            return matchedOptionLabel;
        }

        if (this.shouldAvoidRawDropdownFallback()) {
            return '';
        }

        return this.normalizeDisplayLabel(ReactiveFormValueUtils.formatPreviewValue(controlValue, this.field));
    }

    private resolveMatchedOptionLabel(controlValue: unknown): string {
        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(this.field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(this.field);
        const matchedOption = this.filteredOptions.find((option) =>
            ReactiveFormValueUtils.areValuesEquivalent(option.value, controlValue, primaryKey, identityKeys)
        );

        return this.normalizeDisplayLabel(matchedOption?.label ?? '');
    }

    private shouldAvoidRawDropdownFallback(): boolean {
        const data = this.field?.input?.data as DropdownData<T> | undefined;

        return ReactiveFormValueUtils.isDropdownLikeField(this.field, data);
    }

    private shouldRestorePanelAfterOptionsChange(): boolean {
        return this.isPanelOpen &&
            this.isPanelRestorableField() &&
            this.field.dependencyBehaviour?.preservePanelOnReload !== false;
    }

    private schedulePanelRestore(): void {
        if (this.isRestoreScheduled) {
            return;
        }

        this.isRestoreScheduled = true;

        globalThis.setTimeout((): void => {
            this.isRestoreScheduled = false;
            const panel = this.reactiveSelect ?? this.reactiveMultiselect;

            if (!this.shouldRestoreAfterReload) {
                return;
            }

            this.shouldRestoreAfterReload = false;

            if (!panel || typeof panel.show !== 'function') {
                return;
            }

            if (this.field.dependencyBehaviour?.preserveFilterOnReload !== false && this.lastFilterValue !== '') {
                panel.filterValue = this.lastFilterValue;
            }

            panel.show();
            this.isPanelOpen = true;
        }, PANEL_RESTORE_DELAY_MS);
    }

    private isPanelRestorableField(): boolean {
        return ReactiveFormValueUtils.isSingleDropdownField(this.field) ||
            ReactiveFormValueUtils.isMultiSelectField(this.field);
    }

    private createOptionsSignature(): string {
        const options = this.filteredOptions;
        const optionCount = options.length;

        if (optionCount === DEFAULT_FILTER_MIN_LENGTH) {
            return '0';
        }

        const firstOption = options[DEFAULT_FILTER_MIN_LENGTH];
        const lastOption = options[optionCount - 1];

        return [
            optionCount,
            String(firstOption?.label ?? ''),
            this.readComparableOptionValue(firstOption?.value),
            String(lastOption?.label ?? ''),
            this.readComparableOptionValue(lastOption?.value),
        ].join('|');
    }

    private buildLimitedRenderedOptions(options: readonly FilteredValue<T>[], limit: number): FilteredValue<T>[] {
        const selectedOptions = this.resolveSelectedOptions(options);
        const selectedKeys = new Set(selectedOptions.map((option) => this.readComparableOptionValue(option.value)));
        const limitedOptions = [...selectedOptions];

        for (const option of options) {
            if (limitedOptions.length >= limit) {
                break;
            }

            if (!selectedKeys.has(this.readComparableOptionValue(option.value))) {
                limitedOptions.push(option);
            }
        }

        return limitedOptions;
    }

    private findRenderedOptionsByFilter(
        options: readonly FilteredValue<T>[],
        normalizedFilter: string,
        limit: number,
    ): FilteredValue<T>[] {
        const selectedOptions = this.resolveSelectedOptions(options);
        const selectedKeys = new Set(selectedOptions.map((option) => this.readComparableOptionValue(option.value)));
        const matches = [...selectedOptions];

        for (const option of options) {
            if (matches.length >= limit) {
                break;
            }

            if (selectedKeys.has(this.readComparableOptionValue(option.value))) {
                continue;
            }

            if (this.doesOptionMatchFilter(option, normalizedFilter)) {
                matches.push(option);
            }
        }

        return matches;
    }

    private doesOptionMatchFilter(option: FilteredValue<T>, normalizedFilter: string): boolean {
        return this.normalizeFilterValue(option.label).includes(normalizedFilter) ||
            this.normalizeFilterValue(this.readComparableOptionValue(option.value)).includes(normalizedFilter);
    }

    private resolveSelectedOptions(options: readonly FilteredValue<T>[]): FilteredValue<T>[] {
        const fieldName = this.field?.name ?? '';
        const controlValue = fieldName !== '' ? this.model?.get(fieldName)?.value : null;

        if (!ReactiveFormValueUtils.hasNonEmptyValue(controlValue)) {
            return [];
        }

        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(this.field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(this.field);
        const selectedSignature = this.createSelectedOptionsSignature(options, controlValue);

        if (selectedSignature === this.lastSelectedOptionsSignature) {
            return this.lastSelectedOptions;
        }

        const matchedOption = options.find((option) =>
            ReactiveFormValueUtils.areValuesEquivalent(option.value, controlValue, primaryKey, identityKeys)
        );
        const selectedOptions = matchedOption ? [matchedOption] : [];

        this.lastSelectedOptionsSignature = selectedSignature;
        this.lastSelectedOptions = selectedOptions;

        return selectedOptions;
    }

    private createSelectedOptionsSignature(options: readonly FilteredValue<T>[], controlValue: unknown): string {
        return [
            this.createOptionsSignature(),
            options.length,
            this.readComparableOptionValue(controlValue),
        ].join('|');
    }

    private normalizeFilterValue(value: unknown): string {
        return String(value ?? '')
            .trim()
            .toLocaleLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }

    private readComparableOptionValue(value: unknown): string {
        const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(this.field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(this.field);
        const comparableValue = ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys);

        return String(comparableValue ?? '');
    }

    private normalizeDisplayLabel(value: unknown): string {
        const label = String(value ?? '').trim();

        return ReactiveFormValueUtils.isBadDisplayToken(label) ? '' : label;
    }
}
